import { API } from "@nextgammon/shared";

const API_URL = process.env.BUN_PUBLIC_API_URL;

type MatchmakingEventCallback = (data: any) => void;

interface MatchmakingEventHandlers {
  waiting?: MatchmakingEventCallback;
  matchFound?: MatchmakingEventCallback;
  error?: MatchmakingEventCallback;
}

/**
 * MatchmakingService
 *
 * A service for managing SSE connections to the matchmaking endpoint using native EventSource.
 */
export class MatchmakingService {
  // Renamed from MatchmakingEventService
  private eventSource: EventSource | null = null;
  private handlers: MatchmakingEventHandlers = {};

  async findMatch(
    // Renamed from startMatchmaking
    opponentType: "human" | "cpu",
    handlers: MatchmakingEventHandlers,
    gameMode: string = "standard"
  ): Promise<void> {
    if (this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) {
      console.log("[MatchmakingService] Already matchmaking or connected, disconnecting first.");
      await this.disconnect();
    }

    this.handlers = handlers;
    console.log("[MatchmakingService] Starting matchmaking...");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        const errorMessage = "Authentication token not found.";
        console.error("[MatchmakingService]", errorMessage);
        this.handleError(errorMessage); // This will also disconnect
        return;
      }

      const matchmakingUrl = `${API_URL}/v1/matchmaking/join?opponentType=${opponentType}&gameMode=${gameMode}&token=${token}`;

      this.eventSource = new EventSource(matchmakingUrl);

      this.eventSource.onopen = () => {
        console.log("[MatchmakingService] Connection opened.");
      };

      this.eventSource.onerror = (event) => {
        console.error("[MatchmakingService] Connection error:", event);
        // Simple error handling - browser will auto-reconnect
        const errorMessage = "Matchmaking connection error.";
        this.handleError(errorMessage);
      };

      this.eventSource.addEventListener(
        API.Game.SSEEventType.MATCHMAKING_WAITING,
        (event: MessageEvent) => {
          try {
            const parsedData = JSON.parse(event.data);
            console.log(
              `[MatchmakingService] Received event: ${API.Game.SSEEventType.MATCHMAKING_WAITING}`,
              parsedData
            );
            this.handlers.waiting?.(parsedData);
          } catch (e) {
            console.error(
              "[MatchmakingService] Error parsing MATCHMAKING_WAITING data:",
              e,
              "Raw data:",
              event.data
            );
            this.handleError("Invalid event data for matchmaking-waiting"); // This will also disconnect
          }
        }
      );

      this.eventSource.addEventListener(
        API.Game.SSEEventType.MATCHMAKING_MATCH_FOUND,
        (event: MessageEvent) => {
          try {
            const parsedData = JSON.parse(event.data);
            console.log(
              `[MatchmakingService] Received event: ${API.Game.SSEEventType.MATCHMAKING_MATCH_FOUND}`,
              parsedData
            );
            this.handlers.matchFound?.(parsedData);
            // DO NOT automatically disconnect here. Caller is responsible.
            // this.disconnect();
          } catch (e) {
            console.error(
              "[MatchmakingService] Error parsing MATCHMAKING_MATCH_FOUND data:",
              e,
              "Raw data:",
              event.data
            );
            this.handleError("Invalid event data for matchmaking-match-found"); // This will also disconnect
          }
        }
      );
    } catch (error) {
      console.error("[MatchmakingService] Error setting up matchmaking connection:", error);
      this.handleError(error instanceof Error ? error.message : "Failed to start matchmaking");
    }
  }

  private handleError(message: string): void {
    console.error("[MatchmakingService] Error:", message);
    this.handlers.error?.({ message });
    // For matchmaking, if an error occurs, we typically want to stop and inform the user.
    // Disconnecting here ensures we clean up the current attempt.
    this.disconnect();
  }

  async disconnect(): Promise<void> {
    if (!this.eventSource) {
      return;
    }
    console.log("[MatchmakingService] Disconnecting...");

    this.eventSource.close(); // EventSource.close() is synchronous
    this.eventSource = null;
    this.handlers = {}; // Clear handlers to prevent calls on stale handlers
    console.log("[MatchmakingService] Disconnected.");
  }
}

// Export singleton instance
export const matchmakingService = new MatchmakingService(); // Renamed instance
